import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, BookOpen, ChevronRight, ArrowLeft, Tag, Eye, 
  Filter, School, Building2, Cross 
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const KnowledgeBasePage = () => {
  const { api } = useAuth();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('category', selectedCategory);
      if (selectedTag) params.set('tag', selectedTag);
      const res = await api.get(`/knowledge-base/articles?${params.toString()}`);
      setArticles(res.data.articles || []);
      setCategories(res.data.categories || []);
      setAllTags(res.data.tags || []);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, [selectedCategory, selectedTag]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchArticles();
  };

  const openArticle = async (article) => {
    try {
      const full = await api.get(`/knowledge-base/articles/${article.id}`);
      setSelectedArticle(full.data);
    } catch {
      setSelectedArticle(article);
    }
  };

  const categoryIcons = {
    'Getting Started': BookOpen,
    'Organization Setup': Building2,
    'Progress & Reports': Eye,
    'Billing & Subscriptions': Tag,
    'Curriculum': Cross,
    'FAQ': Search,
  };

  const categoryColors = {
    'Getting Started': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'Organization Setup': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'Progress & Reports': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'Billing & Subscriptions': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    'Curriculum': 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    'FAQ': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  };

  if (selectedArticle) {
    return (
      <div className="space-y-6" data-testid="kb-article-view">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedArticle(null)}
          className="text-slate-400 hover:text-white"
          data-testid="kb-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Knowledge Base
        </Button>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={categoryColors[selectedArticle.category] || 'bg-slate-700 text-slate-300'}>
                {selectedArticle.category}
              </Badge>
              {selectedArticle.audience !== 'all' && (
                <Badge variant="outline" className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20">
                  {selectedArticle.audience === 'school' ? 'For Schools' : 
                   selectedArticle.audience === 'church' ? 'For Churches' : selectedArticle.audience}
                </Badge>
              )}
            </div>
            <CardTitle className="text-2xl text-white">{selectedArticle.title}</CardTitle>
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {selectedArticle.views || 0} views</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert prose-sm max-w-none
              prose-headings:text-slate-100 prose-p:text-slate-300 
              prose-strong:text-white prose-li:text-slate-300
              prose-a:text-emerald-400 prose-code:text-emerald-300
              prose-table:text-slate-300 prose-th:text-white prose-td:text-slate-300
              prose-th:border-slate-600 prose-td:border-slate-700
              prose-hr:border-slate-700"
            >
              <ReactMarkdown
                components={{
                  img: ({node, ...props}) => (
                    <img 
                      {...props} 
                      src={props.src?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${props.src}` : props.src}
                      className="rounded-lg max-w-full h-auto my-4 border border-slate-600"
                      alt={props.alt || 'Article image'}
                      loading="lazy"
                    />
                  )
                }}
              >{selectedArticle.content}</ReactMarkdown>
            </div>
            {selectedArticle.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-slate-700">
                {selectedArticle.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs bg-slate-700/50 text-slate-400 border-slate-600 cursor-pointer hover:bg-slate-600"
                    onClick={() => { setSelectedTag(tag); setSelectedArticle(null); }}
                  >
                    <Tag className="w-3 h-3 mr-1" />{tag}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-base-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-emerald-400" /> Knowledge Base
        </h2>
        <p className="text-slate-400 mt-1">Guides, tutorials, and resources for managing your organization</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="kb-search-input"
          />
        </div>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="kb-search-btn">
          Search
        </Button>
      </form>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === '' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('')}
          className={selectedCategory === '' ? 'bg-emerald-600' : 'border-slate-600 text-slate-300 hover:text-white'}
          data-testid="kb-filter-all"
        >
          All
        </Button>
        {categories.map(cat => (
          <Button
            key={cat}
            variant={selectedCategory === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
            className={selectedCategory === cat ? 'bg-emerald-600' : 'border-slate-600 text-slate-300 hover:text-white'}
            data-testid={`kb-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {cat}
          </Button>
        ))}
      </div>

      {/* Active tag filter */}
      {selectedTag && (
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-400">Filtered by tag:</span>
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            {selectedTag}
            <button onClick={() => setSelectedTag('')} className="ml-1 hover:text-white">&times;</button>
          </Badge>
        </div>
      )}

      {/* Articles Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-40 bg-slate-800/50 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400">No articles found matching your criteria</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {articles.map(article => {
            const CatIcon = categoryIcons[article.category] || BookOpen;
            return (
              <Card 
                key={article.id} 
                className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/30 transition-colors cursor-pointer group"
                onClick={() => openArticle(article)}
                data-testid={`kb-article-card-${article.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      categoryColors[article.category]?.split(' ')[0] || 'bg-slate-700'
                    }`}>
                      <CatIcon className={`w-5 h-5 ${
                        categoryColors[article.category]?.split(' ')[1] || 'text-slate-400'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white group-hover:text-emerald-400 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-slate-700/50 text-slate-400 border-slate-600">
                          {article.category}
                        </Badge>
                        {article.audience !== 'all' && (
                          <Badge variant="outline" className="text-xs bg-indigo-500/10 text-indigo-400 border-indigo-500/20">
                            {article.audience === 'school' ? 'Schools' : 'Churches'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {article.tags?.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs text-slate-500">#{tag}</span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePage;
